import { useEffect, useState } from 'react';
import supabase from '../utils/supabaseClient';
import logger from '../utils/logger';
import { selectDormantSignalLine } from '../utils/dormantSignalLine';

/**
 * D-033: Fetches per-dormant-model envy signal lines for the current user.
 *
 * Returns `{ [modelId]: signalLine }`. Skipped entirely for Pro users (dormant
 * cards aren't rendered to them). Per-session sessionStorage cache keyed by
 * user id avoids re-querying on every mount — surfaces mount-unmount on
 * navigation between `/home` and `/cars`.
 */
export function useDormantSignals({ userId, hasProAccess, enabled = true }) {
  const [signals, setSignals] = useState({});

  useEffect(() => {
    if (!enabled || !userId || hasProAccess) return;
    const cacheKey = `dormant_signals:${userId}`;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        setSignals(cached);
        return;
      }
    } catch { /* ignore */ }

    let cancelled = false;
    supabase.rpc('get_user_dormant_signals').then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        logger.warn('[useDormantSignals] RPC failed', error);
        return;
      }
      const map = {};
      (data || []).forEach((row) => {
        const line = selectDormantSignalLine(row);
        if (line) map[row.model_id] = line;
      });
      try { sessionStorage.setItem(cacheKey, JSON.stringify(map)); } catch { /* ignore quota */ }
      setSignals(map);
    });
    return () => { cancelled = true; };
  }, [userId, hasProAccess, enabled]);

  return signals;
}
